import {FC} from "react"
import {ReactSVG} from "react-svg"

import {RecipeVariantProps, css, cva, cx} from "@styled-system/css"

export type TagColorScheme = RecipeVariantProps<typeof styles>
interface Props {
  title: string
  icon?: string
  noIconColoring?: boolean
  className?: string
}

const styles = cva({
  base: {
    textStyle: "captionRegular",
    borderRadius: 8,
    paddingX: 8,
    paddingY: 4,
    display: "flex",
    flexDirection: "row",
    gap: 6,
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  variants: {
    colorScheme: {
      blue: {
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "lightBlue",
        "& span": {
          color: "transparent",
          bgGradient: "aiAccentPrimary",
          backgroundClip: "text",
        },
      },
      yellow: {
        borderWidth: 1,
        borderColor: "yellow.400",
        color: "yellow.400",
      },
      lightBlue: {},
      lightGrayBlue: {},
      lightYellow: {},
      lightRed: {},
      orange: {},
      /** Status label — soft wash, no border (not a CTA / button chrome).
       * Ink on wash (not accent-on-accent) so sunshine yellow stays readable. */
      recommended: {
        backgroundColor: "background.subtle.brand",
        color: "text.primary",
        borderWidth: 0,
        borderRadius: 4,
        fontWeight: 600,
      },
      positivePrimary: {
        // Ink on wash (same rule as `recommended`) — brand tint lives in the
        // background, not the label. Bodewell body ink is navy #12212E.
        color: "text.primary",
        backgroundColor: "background.brand.primary",
      },
      positive: {
        color: "text.primary",
        backgroundColor: "background.brand.primarySubtle",
      },
      warning: {
        color: "text.brand.accent",
        backgroundColor: "background.warn",
      },
      error: {
        color: "text.error.primary",
        backgroundColor: "background.error",
      },
    },
    transparent: {
      true: {
        color: "white",
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      colorScheme: "lightBlue",
      transparent: true,
      css: {
        backgroundColor: "rgba(231, 240, 251, 0.1)",
        color: "white",
      },
    },
    {
      colorScheme: "lightBlue",
      transparent: false,
      css: {
        backgroundColor: "bgBlue",
        color: "lightBlue",
      },
    },
    {
      colorScheme: "lightGrayBlue",
      transparent: false,
      css: {
        backgroundColor: "background.quaternary",
        color: "lightBlue",
      },
    },
    {
      colorScheme: "lightYellow",
      transparent: true,
      css: {
        backgroundColor: "background.warn",
        color: "text.warning.primary",
      },
    },
    {
      colorScheme: "lightYellow",
      transparent: false,
      css: {
        backgroundColor: "background.warn",
        color: "text.brand.accent",
      },
    },
    {
      colorScheme: "lightRed",
      transparent: true,
      css: {
        backgroundColor: "background.error",
        color: "text.error.primary",
      },
    },
    {
      colorScheme: "lightRed",
      transparent: false,
      css: {
        backgroundColor: "bgRed",
        "& span": {
          color: "transparent",
          bgGradient: "aiAccentDanger",
          backgroundClip: "text",
        },
      },
    },
    {
      colorScheme: "orange",
      transparent: false,
      css: {
        backgroundColor: "transparent",
        color: "text.brand.accent",
        borderWidth: 1,
        borderColor: "border.brand.accent",
        borderStyle: "solid",
      },
    },
    {
      colorScheme: "positive",
      transparent: true,
      css: {
        backgroundColor: "background.tagTransparent",
        color: "base.white",
      },
    },
    {
      colorScheme: "warning",
      transparent: true,
      css: {
        backgroundColor: "background.tagTransparent",
        color: "text.warning.primary",
      },
    },
    {
      colorScheme: "error",
      transparent: true,
      css: {
        backgroundColor: "background.tagTransparent",
        color: "text.error.primary",
      },
    },
  ],
})

export const Tag: FC<Props & TagColorScheme> = ({
  title,
  icon,
  colorScheme,
  transparent = false,
  className,
}) => (
  <div className={cx(styles({colorScheme, transparent}), className)}>
    {icon && (
      <ReactSVG
        src={icon}
        className={cx(
          css({
            height: 18,
            width: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }),
        )}
      />
    )}
    <span>{title}</span>
  </div>
)
